import {
  FC, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { pincodeApi } from 'src/api/pincode';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { Pincode } from 'src/interfaces/masters/pincode/pincode';

interface AddTechnicianServiceAreaProps {
  open: boolean;
  onClose: () => void;
  cpServiceAreaIds: string[];
  serviceAreaIds: string[];
  addServiceAreas: (data: string[]) => Promise<boolean>;
}

export const AddTechnicianServiceArea: FC<AddTechnicianServiceAreaProps> = (props) => {
  const {
    open, onClose, cpServiceAreaIds, serviceAreaIds, addServiceAreas
  } = props;

  const [isAvailable, setIsAvailable] = useState<string>('Loading');
  const [allPincodes, setAllPincodes] = useState<Pincode[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [districts, setDistricts] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [codes, setCodes] = useState<(string | number)[]>([]);
  const [selectedcodes, setSelectedcodes] = useState<(string | number)[]>([]);
  const [duplicateMessage, setDuplicateMessage] = useState<string>('');

  const existingPincodeSet = useMemo(
    () => new Set((serviceAreaIds || []).map((id) => id.toString().split('#')[0])),
    [serviceAreaIds]
  );

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      setSelectedState('');
      setSelectedDistricts([]);
      setSelectedCities([]);
      setSelectedcodes([]);
      setDuplicateMessage('');
      onClose();
    }
  };

  const recomputeCodes = (
    stateValue: string,
    districtValues: string[],
    cityValues: string[]
  ) => {
    let filtered = allPincodes;
    if (stateValue) {
      filtered = filtered.filter((p) => p.state === stateValue);
    }
    if (districtValues.length) {
      filtered = filtered.filter((p) => districtValues.includes(p.district));
    }
    if (cityValues.length) {
      filtered = filtered.filter((p) => cityValues.includes(p.city));
    }

    const uniqueCodes = Array.from(new Set(filtered.map((p) => p.code)))
      .filter((code) => !existingPincodeSet.has(code.toString()));
    setCodes(uniqueCodes);
  };

  const loadPincodesFromCpServiceAreas = async () => {
    if (!cpServiceAreaIds || cpServiceAreaIds.length === 0) {
      setIsAvailable('Fail');
      setAllPincodes([]);
      setStates([]);
      setDistricts([]);
      setCities([]);
      setCodes([]);
      return;
    }

    const pincodeSet = new Set<number>();
    cpServiceAreaIds.forEach((item) => {
      const [code] = item.toString().split('#');
      if (code) {
        pincodeSet.add(Number(code));
      }
    });

    const codeArray = Array.from(pincodeSet);
    const paginationObject: PaginationAndFilter = {
      filters: [
        {
          property: 'code',
          operator: 'in',
          value: codeArray,
        },
      ],
      pageSize: 9999,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };

    const result = await pincodeApi.getPincodesByPaginationAndFilters(paginationObject);
    const records: Pincode[] = result?.responseData?.records || [];

    if (records.length === 0) {
      setIsAvailable('Fail');
      setAllPincodes([]);
      setStates([]);
      setDistricts([]);
      setCities([]);
      setCodes([]);
      return;
    }

    setAllPincodes(records);
    setStates(Array.from(new Set(records.map((p) => p.state))));
    setDistricts([]);
    setCities([]);
    setSelectedState('');
    setSelectedDistricts([]);
    setSelectedCities([]);
    setSelectedcodes([]);
    setIsAvailable('Success');

    const uniqueCodes = Array.from(new Set(records.map((p) => p.code)))
      .filter((code) => !existingPincodeSet.has(code.toString()));
    setCodes(uniqueCodes);
  };

  const handleStateChange = (event): void => {
    const { target: { value } } = event;
    setSelectedState(value);
    setSelectedDistricts([]);
    setSelectedCities([]);
    setSelectedcodes([]);

    const stateDistricts = allPincodes
      .filter((item) => item.state === value)
      .map((item) => item.district);
    setDistricts(Array.from(new Set(stateDistricts)));
    setCities([]);

    recomputeCodes(value, [], []);
  };

  const handleDistChange = (event): void => {
    const { target: { value } } = event;
    setSelectedDistricts(value);
    setSelectedCities([]);
    setSelectedcodes([]);

    const citiesForDist = allPincodes
      .filter((item) => (!selectedState || item.state === selectedState)
        && value.includes(item.district))
      .map((item) => item.city);
    setCities(Array.from(new Set(citiesForDist)));

    recomputeCodes(selectedState, value, []);
  };

  const handleCityChange = (event): void => {
    const { target: { value } } = event;
    setSelectedCities(value);
    setSelectedcodes([]);
    recomputeCodes(selectedState, selectedDistricts, value);
  };

  const handlePincodesChange = (event): void => {
    setSelectedcodes(event.target.value);
  };

  const handleAddServiceAreas = async () => {
    const uniqueSelected = Array.from(new Set(selectedcodes.map((p) => p.toString())));
    const newPincodes = uniqueSelected.filter((p) => !existingPincodeSet.has(p));
    const duplicatePincodes = uniqueSelected.filter((p) => existingPincodeSet.has(p));

    if (duplicatePincodes.length > 0) {
      setDuplicateMessage(`Already added: ${duplicatePincodes.join(', ')}. These were skipped.`);
    } else {
      setDuplicateMessage('');
    }

    if (newPincodes.length === 0) {
      return;
    }

    const addResult = await addServiceAreas(newPincodes);
    if (addResult) {
      closeDialog('', '');
    }
  };

  useEffect(() => {
    if (open) {
      loadPincodesFromCpServiceAreas().catch(console.error);
    }
  }, [open, cpServiceAreaIds]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle sx={{ justifyContent: 'flex-end' }}>
        Add Service Areas
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {isAvailable === 'Loading' && (
        <Typography
          sx={{
            ml: 4,
            mb: 4,
          }}
          variant="body2"
        >
          Loading...
        </Typography>
      )}
      {isAvailable === 'Fail' && (
        <Typography
          sx={{
            ml: 4,
            mb: 4,
          }}
          color="red"
          variant="body2"
        >
          Pincodes Are Not Available
        </Typography>
      )}
      {isAvailable === 'Success' && (
        <>
          <DialogContent
            sx={{
              mt: -2,
            }}
          >
            <Grid
              container
              spacing={1}
            >
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  State
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="state-select"
                    value={selectedState}
                    onChange={(event) => {
                      handleStateChange(event);
                    }}
                    renderValue={(t) => t}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {states?.map((item) => (
                      <MenuItem
                        key={item}
                        value={item}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  District
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="district-multi-select"
                    multiple
                    value={selectedDistricts}
                    onChange={(event) => {
                      handleDistChange(event);
                    }}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {districts.map((district) => (
                      <MenuItem
                        key={district}
                        value={district}
                      >
                        <ListItemIcon>
                          <Checkbox checked={selectedDistricts.indexOf(district) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={district} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Cities
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="city-multi-select"
                    multiple
                    value={selectedCities}
                    onChange={(event) => {
                      handleCityChange(event);
                    }}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {cities.map((city) => (
                      <MenuItem
                        key={city}
                        value={city}
                      >
                        <ListItemIcon>
                          <Checkbox checked={selectedCities.indexOf(city) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={city} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Pincode
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl fullWidth>
                    <Select
                      labelId="pincode-multi-select"
                      multiple
                      value={selectedcodes}
                      onChange={(event) => handlePincodesChange(event)}
                      renderValue={(t) => t.join(', ')}
                      style={{
                        fontSize: '12px',
                        height: '35px',
                        width: '100%',
                      }}
                      MenuProps={MenuProps}
                    >
                      {codes?.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                        >
                          <ListItemIcon>
                            <Checkbox checked={selectedcodes.indexOf(option) > -1} />
                          </ListItemIcon>
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    color="primary"
                    onClick={() => {
                      setSelectedState('');
                      setSelectedDistricts([]);
                      setSelectedCities([]);
                      setSelectedcodes([]);
                      setDuplicateMessage('');
                      const uniqueCodes = Array.from(new Set(allPincodes.map((p) => p.code)))
                        .filter((code) => !existingPincodeSet.has(code.toString()));
                      setCodes(uniqueCodes);
                    }}
                    variant="outlined"
                    sx={{ minWidth: 'auto', height: '35px' }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box
              sx={{
                minHeight: 250,
              }}
            >
              {duplicateMessage && (
                <Typography
                  color="red"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  {duplicateMessage}
                </Typography>
              )}
              {selectedcodes.length > 0 && (
                <Box>
                  <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1 }}
                    variant="h6"
                  >
                    Selected Pincodes:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedcodes.map((code) => (
                      <Chip
                        key={code}
                        label={code}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              alignItems: 'right',
            }}
          >
            <Button
              color="primary"
              onClick={(e) => {
                closeDialog(e, '');
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={selectedcodes.length < 1}
              onClick={() => {
                handleAddServiceAreas().catch(console.error);
              }}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

AddTechnicianServiceArea.defaultProps = {
  open: false,
};

AddTechnicianServiceArea.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  addServiceAreas: PropTypes.func,
  cpServiceAreaIds: PropTypes.array,
  serviceAreaIds: PropTypes.array,
};
